import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Role } from '@abastece/shared';
import { useT } from '../i18n/index.js';
import { etiquetaRol } from '../i18n/dominio.js';
import { db } from '../db/dexie.js';
import { esperaDe, tiempoDeEspera, useEspera } from '../lib/intentos.js';
import { abrirSesion, comprobarPin, type Sesion } from '../lib/sesion.js';
import { TecladoPin } from '../ui/TecladoPin.js';
import { sincronizar } from '../sync/engine.js';
import { Idioma } from '../ui/Idioma.js';
import { Ico } from '../ui/Iconos.js';

export function Entrar({
  usuarioInicial,
  onPinCorrecto,
  onEntro,
}: {
  /** Open on this person's PIN pad rather than the list: whoever was signed in when it locked. */
  usuarioInicial?: string;
  /** The PIN is right and the greeting starts: what has to be ready when it ends can start now. */
  onPinCorrecto?: () => void;
  onEntro?: (sesion: Sesion) => void;
}) {
  const t = useT();
  const usuarios = useLiveQuery(() => db.usuarios.filter((u) => u.activo).toArray(), [], []);
  const [seleccionado, setSeleccionado] = useState<string | null>(usuarioInicial ?? null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [limpiar, setLimpiar] = useState(0);
  /** Right PIN, not in yet: the pad is saying so. */
  const [adentro, setAdentro] = useState<Sesion | null>(null);
  const [espera, esperar] = useEspera();

  // Somebody's pad, opened while a wait from their last wrong PINs is still running: said at once.
  useEffect(() => {
    esperar(seleccionado ? esperaDe({ usuarioId: seleccionado }) : 0);
    // Only when the person changes.
  }, [seleccionado]);

  const usuario = usuarios.find((u) => u.id === seleccionado);

  async function probar(pin: string) {
    if (!seleccionado) return;
    setOcupado(true);
    setError(null);
    const res = await comprobarPin(seleccionado, pin);
    if (!res.ok) {
      setOcupado(false);
      setError(
        res.motivo === 'espera' ? null : t(res.motivo === 'pin-incorrecto' ? 'entrar.pinIncorrecto' : 'entrar.inactivo'),
      );
      setLimpiar((n) => n + 1);
      if (res.esperaMs) esperar(res.esperaMs);
      return;
    }
    // Still `ocupado`: nothing on the pad answers while it says hello.
    setAdentro(res.sesion);
    onPinCorrecto?.();
  }

  async function pasar(quien: Sesion) {
    await abrirSesion(quien);
    onEntro?.(quien);
  }

  /**
   * A device that has never synced has no users cached, so nobody can sign in. Say so plainly
   * and offer the fix, rather than showing an empty list that looks like a bug.
   */
  if (usuarios.length === 0) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-6 p-6 text-center">
        <Idioma tamano="compacto" />
        <h1 className="text-xl font-semibold">{t('entrar.sinUsuarios')}</h1>
        <p className="text-sm text-slate-400">{t('entrar.descargarUsuarios')}</p>
        <button type="button" className="btn-primario" onClick={() => void sincronizar()}>
          <Ico icono="sincronizar" />
          {t('entrar.sincronizar')}
        </button>
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 p-6">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-semibold">{t('entrar.quienAtiende')}</h1>
          <Idioma tamano="compacto" />
        </div>
        <div className="flex flex-col gap-3">
          {usuarios.map((u) => (
            <button
              key={u.id}
              type="button"
              className="btn-secundario w-full justify-between px-5"
              onClick={() => {
                setSeleccionado(u.id);
                setError(null);
              }}
              data-sin-icono="fila"
            >
              <span>{u.nombre}</span>
              <span className="text-sm font-normal text-slate-400">
                {etiquetaRol(u.rol as Role)}
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-6 p-6">
      <header className="text-center">
        <h1 className="text-xl font-semibold">{usuario.nombre}</h1>
        <p className="text-sm text-slate-400">{etiquetaRol(usuario.rol as Role)}</p>
      </header>

      <TecladoPin
        onCompleto={probar}
        ocupado={ocupado || espera > 0}
        error={espera > 0 ? t('pin.espere', { tiempo: tiempoDeEspera(espera, t) }) : error}
        limpiarToken={limpiar}
        exito={adentro ? t('entrar.hola', { nombre: adentro.nombre }) : null}
        onExito={() => adentro && void pasar(adentro)}
      />

      <button
        type="button"
        className="btn-texto gap-1.5 text-sm text-slate-400"
        onClick={() => {
          setSeleccionado(null);
          setError(null);
        }}
      >
        <Ico icono="atras" className="size-4" />
        {t('entrar.cambiarUsuario')}
      </button>
    </div>
  );
}

